import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Clock3, LoaderCircle, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { OrderView, PaymentStatusView } from "../../shared/contracts";
import { api, ApiClientError } from "../api";
import { useCapabilities } from "../capabilities";
import { Brand } from "../components/Brand";

export function PaymentResultPage() {
  const { fubeiEnabled } = useCapabilities();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const stored = readStoredOrder();
  const outTradeNo = params.get("outTradeNo") || params.get("out_trade_no") || stored?.outTradeNo || "";
  const orderId = params.get("orderId");
  const expires = params.get("expires");
  const signature = params.get("signature");
  const statusCapability =
    orderId && outTradeNo && expires && signature
      ? { orderId, outTradeNo, expires, signature }
      : null;
  const [confirmationTimedOut, setConfirmationTimedOut] = useState(false);
  const [confirmationAttempt, setConfirmationAttempt] = useState(0);
  const order = useQuery<OrderView | PaymentStatusView>({
    queryKey: ["payment-result", orderId, outTradeNo, expires, signature],
    queryFn: () =>
      statusCapability
        ? api.fubeiPaymentStatus(statusCapability)
        : api.orderByTradeNo(outTradeNo),
    enabled: Boolean(outTradeNo),
    retry: false,
    refetchInterval: (query) => {
      if (!confirmationTimedOut && query.state.data?.status === "PENDING") {
        return 1_500;
      }
      if (!confirmationTimedOut && statusCapability && query.state.status === "error") {
        return 1_500;
      }
      return false;
    },
  });
  const paid = order.data?.status === "PAID";
  const refundRequired = order.data?.status === "REFUND_REQUIRED";
  const refunded = order.data?.status === "REFUNDED";
  const crossDevice =
    !statusCapability &&
    order.error instanceof ApiClientError &&
    order.error.status === 401 &&
    Boolean(outTradeNo);
  const unavailable = !crossDevice && order.isError;
  const unpaid = order.data && ["FAILED", "CLOSED"].includes(order.data.status);
  const invalid = !outTradeNo;
  const failed = invalid || Boolean(unpaid || refunded || refundRequired);
  const confirmationDelayed = confirmationTimedOut && !paid && !failed && !crossDevice && !unavailable;

  useEffect(() => {
    setConfirmationTimedOut(false);
    if (!outTradeNo || paid || failed || crossDevice) return;

    const timeout = window.setTimeout(() => setConfirmationTimedOut(true), 30_000);
    return () => window.clearTimeout(timeout);
  }, [confirmationAttempt, crossDevice, expires, failed, orderId, outTradeNo, paid, signature]);

  useEffect(() => {
    if (paid) {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      void queryClient.invalidateQueries({ queryKey: ["product-quotes"] });
    }
  }, [paid, queryClient]);

  const retryConfirmation = () => {
    setConfirmationTimedOut(false);
    setConfirmationAttempt((attempt) => attempt + 1);
    void order.refetch();
  };

  const paidDescription = order.data && "quotaTotal" in order.data
    ? `${order.data.subject}已生效，${order.data.durationDays} 天内可使用 ${order.data.quotaTotal} 次生成额度。`
    : "所购权益已到账，请返回原设备或个人中心查看商品和额度。";

  return (
    <main className="result-page">
      <Brand />
      <section className="result-card payment-result-card">
        <span className={paid ? "result-symbol success" : failed ? "result-symbol error" : "result-symbol pending"}>{paid ? <Check size={30} /> : failed ? <X size={30} /> : <Clock3 size={30} />}</span>
        <p className="eyebrow">Alipay confirmation</p>
        <h1>{paid ? "权益已经到账" : refundRequired ? "支付需要人工退款" : refunded ? "款项已经退款" : crossDevice ? "请回到原设备查看结果" : unavailable ? "暂时无法确认支付结果" : confirmationDelayed ? "支付结果仍在确认" : invalid ? "付款结果链接无效" : unpaid ? "支付未完成" : "正在确认支付结果"}</h1>
        <p>{paid ? paidDescription : refundRequired ? "款项已确认，但账户状态或已有权益不允许重复发放，请联系运营处理退款。" : refunded ? "订单款项已进入退款状态，请以支付宝账单中的最终到账结果为准。" : crossDevice ? "支付操作已经提交。当前手机没有原下单账户的登录会话，请回到显示付款二维码的设备查看到账状态。" : unavailable ? "支付可能已经完成，但当前无法取得可信结果。请勿重复付款，稍后重新查询或回到原设备查看。" : confirmationDelayed ? "确认时间较长。请勿重复付款，可稍后重新查询或回到原设备查看。" : invalid ? "返回链接缺少订单信息，请回到 GAMEFRAME 查看账户状态。" : unpaid ? fubeiEnabled ? "订单已经关闭或没有完成支付，你可以回到权益页重新尝试。" : "订单没有完成支付，当前支付功能暂未开放。" : "页面不会仅凭跳转结果发放额度，正在等待支付服务端通知结果。"}</p>
        {!paid && !failed && !crossDevice && !unavailable && !confirmationDelayed && <LoaderCircle className="spin" size={24} />}
        <div className="result-buttons">
          <Link className="button button-primary" to={paid || !fubeiEnabled || crossDevice || unavailable || confirmationDelayed ? "/generate" : "/pricing"}>{paid ? "开始创作" : crossDevice || unavailable || confirmationDelayed ? "返回 GAMEFRAME" : fubeiEnabled ? "返回权益页" : "返回创作页"}</Link>
          {unavailable || confirmationDelayed ? (
            <button className="button button-secondary" type="button" onClick={retryConfirmation}>
              重新查询
            </button>
          ) : !crossDevice ? (
            <Link className="button button-secondary" to="/account">查看账户</Link>
          ) : null}
        </div>
      </section>
    </main>
  );
}

function readStoredOrder(): { outTradeNo?: string } | null {
  try {
    const value = sessionStorage.getItem("gameframe:last-order");
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}
